import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Spin } from 'antd'
import { Field, Form } from 'react-final-form'
import useAction from 'utils/useAction'
import {
  fetchNetworkProgramGoal,
  createNetworkProgramGoal,
  updateNetworkProgramGoal,
} from 'actions/views/earnOffersManagement'
import { selectors as earnOffersSelectors } from 'domains/earnOffersManagement'
import Input from 'components/Input/Input'
import InputNumber from 'components/Input/InputNumber'
import Select from 'components/Select/Select'
import FieldSwitch from 'components/FieldSwitch/FieldSwitch'
import { required } from 'helpers/validators'
import {
  GOAL_TYPE_OPTIONS,
  GOAL_STATUS_OPTIONS,
  GOAL_COMPLIANCE_METRIC_OPTIONS,
} from 'const/earnPrograms'
import {
  StyledForm,
  Footer,
  StyledButton,
} from '../KarmaEarnProgram/KarmaEarnProgram.styles'

const KarmaEarnGoal = ({ id, networkProgramId, closeModal }) => {
  const fetchGoal = useAction(fetchNetworkProgramGoal)
  const createGoal = useAction(createNetworkProgramGoal)
  const updateGoal = useAction(updateNetworkProgramGoal)

  const detail = useSelector(earnOffersSelectors.getGoalDetail)
  const isLoading = useSelector(earnOffersSelectors.getIsGoalLoading)
  const isSaving = useSelector(earnOffersSelectors.getIsGoalSaving)

  useEffect(() => {
    if (id) {
      fetchGoal({ id })
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const initialValues = useMemo(() => {
    if (!id) {
      return { type: 'INSTALL', status: 'ACTIVE', linear: false }
    }
    return {
      goal_id: detail?.goal_id,
      name: detail?.name,
      type: detail?.type,
      position: detail?.position,
      linear: detail?.linear ?? false,
      compliance_metric: detail?.compliance_metric,
      compliance_target: detail?.compliance_target,
      status: detail?.status,
      amount: detail?.amount,
      gross: detail?.gross,
      days_left: detail?.days_left,
    }
  }, [id, detail])

  const onSubmit = (values) => {
    const data = { ...values, network_program_id: networkProgramId }
    if (id) {
      updateGoal({ id, data, callback: closeModal })
    } else {
      createGoal({ data, callback: closeModal })
    }
  }

  if (id && (isLoading || !detail)) {
    return (
      <Spin spinning size="large">
        <div style={{ height: 120 }} />
      </Spin>
    )
  }

  return (
    <Spin spinning={isSaving} size="large">
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <StyledForm onSubmit={handleSubmit}>
            <Field
              required
              name="goal_id"
              label="Goal ID"
              description="External goal identifier, e.g. install"
              component={Input}
              validate={required}
              disabled={!!id}
            />
            <Field name="name" label="Name" component={Input} />
            <Field
              required
              name="type"
              label="Type"
              options={GOAL_TYPE_OPTIONS}
              component={Select}
              validate={required}
              disabled={!!id}
            />
            <Field
              required
              name="status"
              label="Status"
              options={GOAL_STATUS_OPTIONS}
              component={Select}
              validate={required}
            />
            <Field name="position" label="Position" min={0} component={InputNumber} />
            <Field
              type="checkbox"
              name="linear"
              label="Linear"
              component={FieldSwitch}
            />
            <Field
              name="compliance_metric"
              label="Compliance Metric"
              options={GOAL_COMPLIANCE_METRIC_OPTIONS}
              component={Select}
            />
            <Field
              name="compliance_target"
              label="Compliance Target"
              min={0}
              component={InputNumber}
            />
            <Field name="amount" label="Amount" min={0} component={InputNumber} />
            <Field name="gross" label="Gross" min={0} component={InputNumber} />
            <Field name="days_left" label="Days Left" min={0} component={InputNumber} />

            <Footer>
              <StyledButton onClick={closeModal}>Cancel</StyledButton>
              <StyledButton
                type="primary"
                onClick={handleSubmit}
                loading={isSaving}
                disabled={submitting || isSaving}
              >
                {id ? 'Save changes' : 'Create goal'}
              </StyledButton>
            </Footer>
          </StyledForm>
        )}
      />
    </Spin>
  )
}

KarmaEarnGoal.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  networkProgramId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  closeModal: PropTypes.func.isRequired,
}

KarmaEarnGoal.defaultProps = {
  id: null,
}

export default KarmaEarnGoal
